import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createManagementApiKey,
  listManagementApiKeys,
  revokeManagementApiKey,
} from '@kirin/api-client/api/management-api-keys';
import type {
  CreateManagementApiKeyRequest,
  CreateManagementApiKeyResponse,
  ManagementApiKeyList,
  RevokeManagementApiKeyResponse,
} from '@kirin/api-client/contracts/management-api-keys';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { trackEvent } from '@/lib/analytics';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';

/**
 * React Query bindings for organization management API keys (KIR-3748).
 *
 * **The one-time plaintext never enters the query cache.** `create` returns it
 * through `mutateAsync` and nothing here writes it back with `setQueryData`, so
 * it lives only in the calling component's state and is gone on the next
 * render tree teardown or page refresh.
 */

/**
 * List the organization's live management API keys.
 *
 * `retryHandlers.detail` rather than `.list`: the backend answers **404** when
 * the `management_api_keys` flag is off, and `.list` would retry that three
 * times on every Settings load for every flag-off org. Callers should also pass
 * `enabled: false` until the flag and role gate resolve — see
 * `useReleaseManagementApiKeys`.
 *
 * **`includeRevoked` is deliberately left at its default of `false`,** so a key
 * revoked from this card disappears from the list rather than lingering as a
 * dead row — the backend's stated contract for this surface ("revoked keys are
 * hidden by default so the Settings list shows live keys only") and the
 * ticket's own acceptance criterion. That does *not* make
 * `managementKeyStatus`'s `'Revoked'` branch dead code: see its JSDoc for the
 * case that still reaches it.
 */
export function useManagementApiKeys(
  organizationId: string,
  enabled = true
): UseQueryResult<ManagementApiKeyList, unknown> {
  return useQuery({
    queryKey: queryKeys.organizations.managementApiKeys(organizationId),
    queryFn: () => listManagementApiKeys(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    retry: retryHandlers.detail,
  });
}

/**
 * Mint a management API key.
 *
 * Use `mutateAsync` and hold the returned `accessKey` in component state: it is
 * the only time the plaintext is ever available.
 */
export function useCreateManagementApiKey(
  organizationId: string
): UseMutationResult<
  CreateManagementApiKeyResponse,
  unknown,
  CreateManagementApiKeyRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateManagementApiKeyRequest) =>
      createManagementApiKey(apiClient, organizationId, body),
    onSuccess: (_data, variables) => {
      trackEvent('management_api_key_created', {
        organization_id: organizationId,
        has_expiration: variables.expiresAt !== undefined,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.managementApiKeys(organizationId),
      });
    },
  });
}

/**
 * Revoke a management API key. Invalidates the list so the row's status
 * refreshes from the server rather than being patched locally.
 */
export function useRevokeManagementApiKey(
  organizationId: string
): UseMutationResult<RevokeManagementApiKeyResponse, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) =>
      revokeManagementApiKey(apiClient, organizationId, keyId),
    onSuccess: () => {
      trackEvent('management_api_key_revoked', {
        organization_id: organizationId,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.managementApiKeys(organizationId),
      });
    },
  });
}
